/* frontend/src/pages/WelcomePage.js */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { authApi, api } from "../api/client";

const GENERIC_QUESTIONS = new Set([
  "What is your last name?",
  "What house are you in?",
]);

/* Tiny breakpoint hook */
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

function isStudentDomainAccount(meData) {
  const email = String(meData?.email || "").trim().toLowerCase();
  const username = String(meData?.username || "").trim().toLowerCase();
  return (
    email.endsWith("@student.propelschools.org") ||
    username.endsWith("@student.propelschools.org")
  );
}

/**
 * Determine whether user should be treated as educator.
 * Deploy-safe + defensive:
 * - Treat ADMIN as educator (educator+admin users come back as role="admin")
 * - Accept staff flags
 * - Block student-domain accounts as safety net
 */
function isEducatorUser(meData) {
  const role = String(meData?.role || "").trim().toUpperCase();

  // Role-based (canonical if backend provides it)
  if (role === "EDUCATOR" || role === "TEACHER" || role === "STAFF" || role === "ADMIN") {
    return !isStudentDomainAccount(meData);
  }

  // Hint-based (older builds / backend variations)
  const hints = !!(
    meData?.is_staff ||
    meData?.is_superuser ||
    meData?.educator_profile ||
    meData?.educator ||
    meData?.teacher_profile
  );

  // Student email domain safety check
  return hints && !isStudentDomainAccount(meData);
}

/**
 * Keep admin portal aligned with the current environment:
 * - dev.propelhabitpoints.org -> https://api-dev.propelhabitpoints.org/admin
 * - otherwise -> https://api.propelhabitpoints.org/admin
 */
function getAdminPortalUrl() {
  try {
    if (typeof window !== "undefined" && window.location && window.location.hostname) {
      const h = (window.location.hostname || "").toLowerCase();
      if (h === "dev.propelhabitpoints.org" || h.startsWith("dev.")) {
        return "https://api-dev.propelhabitpoints.org/admin";
      }
    }
  } catch {}
  return "https://api.propelhabitpoints.org/admin";
}

export default function WelcomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  function computePostLoginPath(roleOrMe) {
    const publicPaths = new Set(["/", "/welcome", "/login", "/signup", "/recover", "/recover/educator", "/recover/scholar"]);
    const fromPath = location.state?.from?.pathname || "";
    if (fromPath && !publicPaths.has(fromPath)) return fromPath;

    // Accept either a role string or the full /me payload
    const role =
      typeof roleOrMe === "string"
        ? String(roleOrMe || "").trim().toUpperCase()
        : String(roleOrMe?.role || "").trim().toUpperCase();

    const educator =
      typeof roleOrMe === "object" && roleOrMe
        ? isEducatorUser(roleOrMe)
        : (role === "EDUCATOR");

    return educator ? "/dashboard" : "/my-points";
  }

  async function routeWithEducatorSecurityGate(meData) {
    const role = String(meData?.role || "").trim().toUpperCase();
    const educator = isEducatorUser(meData);

    console.debug("[Welcome] routeWithEducatorSecurityGate role:", role, "educator?", educator);

    if (!educator) {
      const to = computePostLoginPath(meData);
      console.debug("[Welcome] Non-educator →", to);
      navigate(to, { replace: true });
      return;
    }

    try {
      console.debug("[Welcome] Fetching educator security status…");

      // ✅ Correct endpoint (backend): /api/auth/security-question/status/
      // Try both variants (trailing slash tolerant)
      let status = await api.get("/api/auth/security-question/status/");
      if (!status?.ok) status = await api.get("/api/auth/security-question/status");

      console.debug("[Welcome] /security-question/status res:", status?.status, status);

      if (!status?.ok) {
        console.warn("[Welcome] Status not ok; sending to /dashboard");
        navigate("/dashboard", { replace: true });
        return;
      }

      const { has_security_question, is_generic, question } = status.data || {};
      const isPresetGeneric =
        is_generic === true ||
        (typeof question === "string" && GENERIC_QUESTIONS.has(question.trim()));

      if (!has_security_question || isPresetGeneric) {
        const to = "/educator/security/setup";
        console.debug("[Welcome] Needs setup →", to);
        navigate(to, { replace: true, state: { from: computePostLoginPath(meData) } });
      } else {
        const to = computePostLoginPath(meData);
        console.debug("[Welcome] Security good →", to);
        navigate(to, { replace: true });
      }
    } catch (e) {
      console.error("[Welcome] Status check error → dashboard", e);
      navigate("/dashboard", { replace: true });
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (busy) return;

    setBusy(true);
    setError('');

    try {
      const ident = (email || "").trim();
      if (!ident || !pw) {
        setError("Please enter your email/username and password.");
        setBusy(false);
        return;
      }

      console.debug("[Welcome] Submitting login for:", ident);
      const res = await authApi.login(ident, pw);
      console.debug("[Welcome] authApi.login res:", res);

      if (!res || typeof res.ok === "undefined") {
        console.error("[Welcome] authApi.login returned unexpected value:", res);
        setError("Login failed: unexpected response from client.");
        setPw("");
        setBusy(false);
        return;
      }

      if (!res.ok) {
        const d = res.data;
        const detail = typeof d === "string" ? d : (d?.detail || d?.message);
        const msg =
          res.status === 401 ? (detail || "Invalid email/username or password.") :
          res.status === 403 ? (detail || "Account not active.") :
          res.status === 404 ? "Login endpoint not found. Check API base." :
          (detail || `Login failed (${res.status}).`);
        console.warn("[Welcome] Login not ok:", res.status, msg);
        setError(msg);
        setPw("");
        setBusy(false);
        return;
      }

      console.debug("[Welcome] Login ok, fetching /api/auth/me…");
      const me = await api.get("/api/auth/me/");
      console.debug("[Welcome] /api/auth/me res:", me?.status, me);

      if (!me?.ok) {
        const d = me?.data;
        const msg = typeof d === "string" ? d : (d?.detail || "Could not load your profile.");
        console.warn("[Welcome] /me not ok:", me?.status, msg);
        setError(msg);
        setPw("");
        setBusy(false);
        return;
      }

      await routeWithEducatorSecurityGate(me.data);
      // navigation happens in routeWithEducatorSecurityGate
    } catch (err) {
      console.error("[Welcome] onSubmit error:", err);
      setError(err?.message || "Network/CORS error.");
      setPw("");
      setBusy(false);
      setBusy(false);
    }
  }

  const s = computeStyles(isMobile);
  const adminPortalUrl = getAdminPortalUrl();

  return (
    <div style={s.page}>
      {/* LEFT: Welcome / brand side */}
      <section style={s.left}>
        <div style={s.leftCard}>
          <img
            src="/icons/Propel_schools_logo.png"
            alt="Propel Schools"
            style={s.logo}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />

          <h1 style={s.h1}>Welcome!</h1>
          <p style={s.lede}>
            Sign in to see the live leaderboard and award points that build great habits.
          </p>
        </div>
      </section>

      {/* RIGHT: Sign In form */}
      <section style={s.right}>
        <div style={s.panel}>
          <h2 style={s.title}>Sign In</h2>

          <form onSubmit={onSubmit} style={s.form} noValidate>
            <label style={s.label} htmlFor="email">Username or email</label>
            <input
              id="email"
              type="text"
              placeholder="your.name@propelschools.org"
              value={email}
              onChange={(e)=>setEmail(e.target.value)}
              style={s.input}
              required
              disabled={busy}
              autoComplete="username email"
              inputMode="email"
              name="username"
            />

            <label style={s.label} htmlFor="pw">Password</label>
            <input
              id="pw"
              type="password"
              placeholder="••••••••"
              value={pw}
              onChange={(e)=>setPw(e.target.value)}
              style={s.input}
              required
              disabled={busy}
              autoComplete="current-password"
              name="password"
            />

            <div style={s.linksRow}>
              <label style={{ userSelect:'none', display: 'inline-flex', alignItems: 'center' }}>
                <input type="checkbox" style={{ marginRight: 8 }} /> Remember me
              </label>
              <Link to="/recover" style={s.smallLink}>Forgot password?</Link>
            </div>

            <button type="submit" style={s.primaryBtn} disabled={busy}>
              {busy ? 'Signing in…' : 'Sign In'}
            </button>

            {error && <div style={s.error}>{error}</div>}
          </form>

          <div style={s.footerRow}>
            New here?&nbsp;
            <Link to="/signup" style={s.smallLink}>Create an Account</Link>
            <span style={{ fontSize: 12, color: '#7C878E' }}>&nbsp;(Educators & Scholars)</span>
          </div>

          {/* Admin Portal link */}
          <div style={{ marginTop: 12 }}>
            <a
              href={adminPortalUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 12, color: '#888', textDecoration: 'underline' }}
            >
              Admin Portal
            </a>
            <span style={{ fontSize: 12, color: '#9aa8b3' }}> (staff only)</span>
          </div>
        </div>
      </section>
    </div>
  );
}

/** Responsive styles */
function computeStyles(isMobile) {
  return {
    page: {
      minHeight: '100vh',
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'minmax(320px, 1fr) 520px',
      background: '#F7F9FC'
    },

    /* Left side */
    left: {
      background: 'linear-gradient(135deg, #5789be 0%, #89c3e8 100%)',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '32px 20px' : '64px'
    },
    leftCard: {
      maxWidth: isMobile ? 480 : 560,
      textAlign: isMobile ? 'center' : 'left'
    },
    logo: {
      width: isMobile ? 180 : 260,
      height: 'auto',
      marginBottom: isMobile ? 20 : 32
    },
    h1: {
      fontSize: isMobile ? 36 : 52,
      lineHeight: 1.1,
      margin: isMobile ? '4px 0 12px' : '8px 0 16px',
      fontWeight: 800
    },
    lede: {
      fontSize: isMobile ? 16 : 18,
      opacity: 0.95,
      maxWidth: isMobile ? '100%' : 520,
      margin: '0 auto'
    },

    /* Right side (form) */
    right: {
      background: '#FFFFFF',
      borderLeft: isMobile ? 'none' : '1px solid rgba(0,0,0,0.05)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '24px 16px 32px' : '48px'
    },
    panel: { width: '100%', maxWidth: 420 },
    title: {
      fontSize: isMobile ? 28 : 36,
      fontWeight: 800,
      color: '#003e73',
      marginBottom: 24,
      textAlign: isMobile ? 'center' : 'left'
    },

    form: { display: 'grid', gap: 8 },
    label: { fontWeight: 700, color: '#042d60', marginTop: 6 },

    input: {
      width: '100%',
      height: 48,
      borderRadius: 16,
      padding: '0 16px',
      border: '1px solid rgba(0,0,0,0.12)',
      outline: 'none',
      marginTop: 2,
      fontSize: 16,
      boxSizing: 'border-box'
    },

    linksRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: 14,
      marginTop: 4,
      color: '#042d60',
      gap: 8,
      flexWrap: isMobile ? 'wrap' : 'nowrap'
    },
    smallLink: { color: '#003e73', textDecoration: 'underline' },

    primaryBtn: {
      width: '100%',
      height: 48,
      borderRadius: 999,
      border: 'none',
      fontWeight: 800,
      cursor: 'pointer',
      marginTop: 12,
      background: '#f9a21a',
      color: 'white',
      fontSize: 16
    },

    error: { marginTop: 8, color: '#b00020', fontSize: 14 },

    footerRow: {
      marginTop: 12,
      fontSize: 14,
      color: '#042d60',
      textAlign: isMobile ? 'center' : 'left'
    },
  };
}
